/**
 * Viewport — Pan/zoom state and coordinate transforms
 * Per PLAN.md core/Viewport.js
 */
import { eventBus, Events } from './EventBus.js';

export class Viewport {
    constructor() {
        // Pan offset (in world coordinates)
        this.x = 0;
        this.y = 0;

        // Zoom level (1.0 = 100%)
        this.zoom = 1.0;

        // Zoom constraints per PRD §4.2
        this.minZoom = 0.25;  // 25%
        this.maxZoom = 4.0;   // 400%

        // Canvas dimensions
        this.width = window.innerWidth;
        this.height = window.innerHeight;
    }

    /**
     * Convert screen coordinates to world coordinates
     * @param {number} screenX 
     * @param {number} screenY 
     * @returns {{x: number, y: number}}
     */
    screenToWorld(screenX, screenY) {
        return {
            x: (screenX - this.width / 2) / this.zoom + this.x,
            y: (screenY - this.height / 2) / this.zoom + this.y
        };
    }

    /**
     * Convert world coordinates to screen coordinates
     * @param {number} worldX 
     * @param {number} worldY 
     * @returns {{x: number, y: number}}
     */
    worldToScreen(worldX, worldY) {
        return {
            x: (worldX - this.x) * this.zoom + this.width / 2,
            y: (worldY - this.y) * this.zoom + this.height / 2
        };
    }

    /**
     * Pan the viewport by delta
     * @param {number} dx 
     * @param {number} dy 
     */
    pan(dx, dy) {
        this.x -= dx / this.zoom;
        this.y -= dy / this.zoom;
        eventBus.emit(Events.CANVAS_PAN, { x: this.x, y: this.y });
        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Zoom at a specific screen point
     * @param {number} delta - Zoom delta (positive = zoom in)
     * @param {number} screenX - Screen X position
     * @param {number} screenY - Screen Y position
     */
    zoomAt(delta, screenX, screenY) {
        const worldBefore = this.screenToWorld(screenX, screenY);

        // Adaptive zoom factor: smaller when zoomed in for finer control
        // Ranges from 1.02 (2%) at min zoom to 1.05 (5%) at max zoom
        const zoomRange = this.maxZoom - this.minZoom;
        const zoomProgress = (this.zoom - this.minZoom) / zoomRange;
        const baseFactor = 1.02 + (zoomProgress * 0.03); // 1.02 to 1.05
        const zoomFactor = delta > 0 ? baseFactor : (1 / baseFactor);
        const newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoom * zoomFactor));

        if (newZoom === this.zoom) return;

        this.zoom = newZoom;

        // Adjust pan to keep point under cursor
        const worldAfter = this.screenToWorld(screenX, screenY);
        this.x += worldBefore.x - worldAfter.x;
        this.y += worldBefore.y - worldAfter.y;

        eventBus.emit(Events.CANVAS_ZOOM, { zoom: this.zoom });
        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Set zoom level directly
     * @param {number} zoom 
     */
    setZoom(zoom) {
        this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, zoom));
        eventBus.emit(Events.CANVAS_ZOOM, { zoom: this.zoom });
        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Reset viewport to center
     */
    reset() {
        this.x = 0;
        this.y = 0;
        this.zoom = 1.0;
        eventBus.emit(Events.CANVAS_PAN, { x: this.x, y: this.y });
        eventBus.emit(Events.CANVAS_ZOOM, { zoom: this.zoom });
        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Update canvas dimensions
     * @param {number} width 
     * @param {number} height 
     */
    resize(width, height) {
        this.width = width;
        this.height = height;
        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Fit viewport to show all nodes
     * @param {Array} nodes - Array of nodes with position and size
     */
    fitToContent(nodes) {
        if (!nodes || nodes.length === 0) {
            this.reset();
            return;
        }

        // Calculate bounds
        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;

        for (const node of nodes) {
            minX = Math.min(minX, node.position.x);
            minY = Math.min(minY, node.position.y);
            maxX = Math.max(maxX, node.position.x + (node.size?.width || 100));
            maxY = Math.max(maxY, node.position.y + (node.size?.height || 50));
        }

        // Add padding
        const padding = 100;
        minX -= padding;
        minY -= padding;
        maxX += padding;
        maxY += padding;

        // Calculate required zoom
        const contentWidth = maxX - minX;
        const contentHeight = maxY - minY;
        const zoomX = this.width / contentWidth;
        const zoomY = this.height / contentHeight;

        this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, Math.min(zoomX, zoomY)));

        // Center on content
        this.x = (minX + maxX) / 2;
        this.y = (minY + maxY) / 2;

        eventBus.emit(Events.CANVAS_PAN, { x: this.x, y: this.y });
        eventBus.emit(Events.CANVAS_ZOOM, { zoom: this.zoom });
        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Serialize viewport state
     * @returns {{x: number, y: number, zoom: number}}
     */
    toJSON() {
        return {
            x: this.x,
            y: this.y,
            zoom: this.zoom
        };
    }

    /**
     * Restore viewport from state
     * @param {{x: number, y: number, zoom: number}} state 
     */
    fromJSON(state) {
        this.x = state.x ?? 0;
        this.y = state.y ?? 0;
        this.zoom = state.zoom ?? 1.0;
    }
}
